<template>
  <section class="container py-5" style="max-width:720px">
    <div class="swap-card card p-3 p-md-4 shadow">
      <!-- Card header: title, network badge, settings -->
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="fw-semibold">Swap</div>
        <div class="d-flex align-items-center gap-2">
          <span class="badge rounded-pill badge-tinted small">{{ isTestnet ? 'Testnet' : 'Mainnet' }}</span>
          <button class="icon-btn" @click="toggleSettings" aria-label="Swap settings">
            <i class="bi bi-gear"></i>
          </button>
        </div>
      </div>

      <!-- Settings panel -->
      <div v-if="showSettings" class="settings-panel surface-2 rounded p-3 mb-3">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <div class="fw-semibold small">Slippage tolerance</div>
          <div class="small text-muted-token">Auto applies to min received</div>
        </div>
        <div class="d-flex flex-wrap gap-2 align-items-center">
          <button type="button" class="btn btn-sm" :class="slippage === 0.1 ? 'btn-primary' : 'btn-outline-secondary'" @click="setSlippage(0.1)">0.1%</button>
          <button type="button" class="btn btn-sm" :class="slippage === 0.5 ? 'btn-primary' : 'btn-outline-secondary'" @click="setSlippage(0.5)">0.5%</button>
          <button type="button" class="btn btn-sm" :class="slippage === 1 ? 'btn-primary' : 'btn-outline-secondary'" @click="setSlippage(1)">1.0%</button>
          <div class="input-group input-group-sm" style="width: 140px;">
            <span class="input-group-text">Custom</span>
            <input v-model.number="slippage" type="number" step="0.1" min="0" class="form-control" @change="persistSlippage" />
            <span class="input-group-text">%</span>
          </div>
        </div>
      </div>

      <!-- From row -->
      <div class="token-row mb-2">
        <div class="d-flex justify-content-between align-items-baseline mb-1">
          <label class="form-label mb-0">From</label>
          <small class="text-muted-token">
            Balance: {{ fromBalance }}
            <button type="button" class="btn btn-link btn-sm p-0 ms-2 align-baseline" @click="useMax" :disabled="!Number(fromBalance)">Max</button>
          </small>
        </div>
        <div class="swap-input">
          <input v-model="amountIn" type="number" inputmode="decimal" class="amount-input form-control" placeholder="0.0" />
          <select class="token-select form-select" v-model="fromAddr">
            <option v-for="t in tokens" :key="t.address" :value="t.address">{{ t.symbol }}</option>
          </select>
        </div>
      </div>

      <!-- Flip button -->
      <div class="d-flex justify-content-center my-2">
        <button class="swap-toggle" @click="flipTokens" aria-label="Switch tokens">
          <i class="bi bi-arrow-down-up"></i>
        </button>
      </div>

      <!-- To row -->
      <div class="token-row">
        <div class="d-flex justify-content-between align-items-baseline mb-1">
          <label class="form-label mb-0">To</label>
          <small class="text-muted-token">Balance: {{ toBalance }}</small>
        </div>
        <div class="swap-input">
          <input :value="quoteOutDisplay" class="amount-input form-control" placeholder="0.0" disabled />
          <select class="token-select form-select" v-model="toAddr">
            <option v-for="t in toOptions" :key="t.address" :value="t.address">{{ t.symbol }}</option>
          </select>
        </div>
        <div class="d-flex justify-content-between small mt-1">
          <div class="text-muted-token d-inline-flex align-items-center gap-2" v-if="loadingQuote">
            <lottie-player
              class="lottie-inline"
              :src="lottieRouteSrc"
              background="transparent"
              speed="1"
              loop
              autoplay
              aria-hidden="true"
            ></lottie-player>
            <span>Finding best route…</span>
          </div>
          <div class="text-danger" v-else-if="quoteErr">{{ quoteErr }}</div>
          <div class="text-muted-token" v-else>Quote updates automatically</div>
          <div class="text-muted-token">{{ isTestnet ? 'Testnet' : 'Mainnet' }}</div>
        </div>
      </div>

      <!-- Summary: price, slippage, fee -->
      <div class="surface-2 rounded p-3 mt-3">
        <div class="d-flex justify-content-between small mb-1">
          <span>Price</span>
          <span v-if="priceText">{{ priceText }}</span>
          <span v-else class="text-muted-token">—</span>
        </div>
        <div class="d-flex justify-content-between small mb-1">
          <span>Slippage</span>
          <span>{{ slippage.toFixed(2) }}%</span>
        </div>
        <div class="d-flex justify-content-between small">
          <span>Pool fee</span>
          <span>{{ ((usedFee || poolFee) / 10000).toFixed(2) }}%</span>
        </div>
      </div>

      <!-- Primary actions -->
      <div class="mt-3">
        <button v-if="!wallet.isConnected" class="btn btn-primary w-100" @click="connect">Connect Wallet</button>
        <button v-else class="btn btn-success w-100" :disabled="!canSwapUI" @click="doSwap">
          {{ swapping ? 'Swapping…' : (insufficientBalance ? 'Insufficient balance' : 'Swap') }}
        </button>
      </div>

      <!-- Suggestions -->
      <div class="mt-3 p-3 rounded surface-2" v-if="toSuggestions.length">
        <div class="fw-semibold mb-2">Swap options (Uniswap)</div>
        <div class="d-flex flex-wrap gap-2">
          <span v-for="t in toSuggestions" :key="t.address" class="badge text-bg-secondary">{{ t.symbol }}</span>
        </div>
      </div>

      <!-- Details -->
      <details class="mt-3">
        <summary class="small fw-semibold">Contract details</summary>
        <div class="small mt-2">
          <div class="d-flex justify-content-between"><span>Network</span><span>{{ isTestnet ? 'Testnet' : 'Mainnet' }} — {{ chain.current.label }}</span></div>
          <div class="d-flex justify-content-between"><span>Quoter</span><span>
            <a v-if="quoterAddr" :href="addressLink(quoterAddr)" target="_blank" rel="noopener">{{ shortAddr(quoterAddr) }}</a>
            <span v-else class="text-muted-token">Not configured</span>
          </span></div>
          <div class="d-flex justify-content-between"><span>Router</span><span>
            <a v-if="routerAddr" :href="addressLink(routerAddr)" target="_blank" rel="noopener">{{ shortAddr(routerAddr) }}</a>
            <span v-else class="text-muted-token">Not configured</span>
          </span></div>
          <div class="d-flex justify-content-between"><span>Pool fee</span><span>{{ ((usedFee || poolFee) / 10000).toFixed(2) }}%</span></div>
          <div class="d-flex justify-content-between"><span>From token</span><span>
            <template v-if="selectedFrom">
              {{ selectedFrom.symbol }}
              · <a :href="tokenLink(selectedFrom.address)" target="_blank" rel="noopener">{{ shortAddr(selectedFrom.address) }}</a>
            </template>
            <span v-else class="text-muted-token">Not selected</span>
          </span></div>
          <div class="d-flex justify-content-between"><span>To token</span><span>
            <template v-if="selectedTo">
              {{ selectedTo.symbol }}
              · <a :href="tokenLink(selectedTo.address)" target="_blank" rel="noopener">{{ shortAddr(selectedTo.address) }}</a>
            </template>
            <span v-else class="text-muted-token">Not selected</span>
          </span></div>
        </div>
      </details>
      <p class="text-muted-token small mt-3">Non‑custodial · Verify contract addresses</p>
      <div v-if="txHash" class="alert alert-success mt-3">
        Success! <a :href="explorerTx" target="_blank" rel="noopener">View on explorer</a>
      </div>
      <div v-if="swapErr" class="alert alert-danger mt-3">{{ swapErr }}</div>
    </div>
  </section>
  
</template>
<script setup lang="ts">
import { onMounted, onBeforeUnmount, computed, ref, watch } from 'vue';
import { formatUnits } from 'viem';
import { useWalletStore } from '../stores/wallet';
import { useTokens } from '../composables/tokens';
import { useQuote } from '../composables/quote';
import { useSwap } from '../composables/swap';
import { usePools } from '../composables/pools';
import { appKitReady } from '../lib/appkit';
import { useToastStore } from '../stores/toast';
import { useChainStore } from '../stores/chain';

const wallet = useWalletStore();
wallet.init();

const { tokens, selectedFrom, selectedTo, fromBalance, toBalance, load, refreshBalances } = useTokens();
const fromAddr = ref<string>('');
const toAddr = ref<string>('');
const amountIn = ref('');
const amountValid = computed(() => Number(amountIn.value) > 0);

const { loading: loadingQuote, error: quoteErrRef, amountOut, usedFee, routePath, getQuote } = useQuote();
const { loading: swapping, error: swapErrRef, txHash, approve, swapExactInputSingle } = useSwap();
const { suggestions, suggestFor } = usePools();

const chain = useChainStore();
const poolFee = computed(() => Number(chain.current.uniswap?.poolFee || 3000));
const isTestnet = computed(() => chain.current.id === 11155111);
const quoterAddr = computed(() => chain.current.uniswap?.quoter || '');
const routerAddr = computed(() => chain.current.uniswap?.router || '');

const quoteOut = computed(() => amountOut.value);
const quoteErr = computed(() => quoteErrRef.value);
const swapErr = computed(() => swapErrRef.value);

const quoteOutFormatted = computed(() => {
  if (!quoteOut.value || !selectedTo.value) return '';
  return `${formatUnits(BigInt(quoteOut.value), selectedTo.value.decimals)} ${selectedTo.value.symbol}`;
});

const quoteOutNormalized = computed(() => {
  if (!quoteOut.value || !selectedTo.value) return 0;
  try { return Number(formatUnits(BigInt(quoteOut.value), selectedTo.value.decimals)); } catch { return 0; }
});

const amountInNumber = computed(() => Number(amountIn.value || '0'));
const priceText = computed(() => {
  if (!selectedFrom.value || !selectedTo.value) return '';
  if (!amountInNumber.value || !quoteOutNormalized.value) return '';
  const p = quoteOutNormalized.value / amountInNumber.value;
  if (!isFinite(p) || p <= 0) return '';
  return `1 ${selectedFrom.value.symbol} ≈ ${p.toPrecision(6)} ${selectedTo.value.symbol}`;
});

// Slippage tolerance (%), persisted
const slippage = ref<number>(Number(localStorage.getItem('swap_slippage') || '0.5'));
function persistSlippage() {
  if (Number.isNaN(slippage.value) || slippage.value < 0) slippage.value = 0.5;
  localStorage.setItem('swap_slippage', String(slippage.value));
}
function setSlippage(pct: number) { slippage.value = pct; persistSlippage(); }
const slippageBps = computed(() => Math.round((slippage.value || 0) * 100));
const minOutStr = computed(() => {
  if (!quoteOut.value) return '0';
  const q = BigInt(quoteOut.value);
  const bps = BigInt(10000 - slippageBps.value);
  return (q * bps / 10000n).toString();
});

const quoteOutDisplay = computed(() => {
  // Only show output when current input is valid
  if (!amountInNumber.value || !quoteOut.value || !selectedTo.value) return '';
  return `${formatUnits(BigInt(quoteOut.value), selectedTo.value.decimals)}`;
});

const insufficientBalance = computed(() => {
  const bal = Number(fromBalance.value || '0');
  const needed = Number(amountIn.value || '0');
  return wallet.isConnected && needed > 0 && bal < needed;
});

const canSwapUI = computed(() => wallet.isConnected && !!quoteOut.value && !swapping.value && amountInNumber.value > 0 && !insufficientBalance.value);
const showSettings = ref(false);
function toggleSettings() { showSettings.value = !showSettings.value; }
const shortAddress = computed(() => wallet.address ? `${wallet.address.slice(0,6)}…${wallet.address.slice(-4)}` : '');
const explorerBase = computed(() => chain.current.explorerBase || 'https://sepolia.etherscan.io');
const explorerTx = computed(() => txHash.value ? `${explorerBase.value}/tx/${txHash.value}` : '#');
const toSuggestions = computed(() => suggestions.value);
const toOptions = computed(() => toSuggestions.value.length ? toSuggestions.value : tokens.value);

function shortAddr(addr?: string) {
  if (!addr) return '';
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}
function addressLink(addr: string) {
  return `${explorerBase.value}/address/${addr}`;
}
function tokenLink(addr: string) {
  const base = explorerBase.value;
  if (/scan\.io|etherscan|arbiscan|polygonscan|basescan/.test(base)) {
    return `${base}/token/${addr}`;
  }
  return `${base}/address/${addr}`;
}

// Theme-aware route-finder Lottie source
const isDarkTheme = ref((document.documentElement.getAttribute('data-theme') || 'light') === 'dark');
const lottieRouteSrc = computed(() => isDarkTheme.value ? '/assets/Cosmos%20light.json' : '/assets/Cosmos.json');
let themeObserver: MutationObserver | null = null;
function syncTheme() {
  isDarkTheme.value = (document.documentElement.getAttribute('data-theme') || 'light') === 'dark';
}

async function connect() {
  if (appKitReady()) {
    try {
      // Safely import and open AppKit modal
      const mod = await import('@reown/appkit/vue-core');
      const { open } = mod.useAppKit();
      await open();
      return;
    } catch (e) {
      // fall through to injected
    }
  }
  await wallet.openModal();
  if (!wallet.isConnected) {
    toast.push('error', 'Unable to open wallet modal. Set VUE_APP_WALLETCONNECT_PROJECT_ID or use injected wallet (MetaMask).');
  } else {
    toast.push('info', 'Connected with injected wallet');
  }
}

async function doQuote() {
  await getQuote(amountIn.value || '0');
  if (quoteErr.value) toast.push('error', quoteErr.value);
}

const toast = useToastStore();
async function doSwap() {
  if (!amountIn.value) return;
  // Approve unlimited for demo simplicity
  await approve(amountIn.value);
  const minOut = minOutStr.value;
  await swapExactInputSingle(amountIn.value, minOut, usedFee?.value || undefined, routePath?.value || null);
  if (swapErr.value) toast.push('error', swapErr.value);
  else if (txHash.value) toast.push('success', 'Swap submitted');
}

onMounted(async () => {
  await load();
  // initialize selects
  if (selectedFrom.value) fromAddr.value = selectedFrom.value.address;
  if (selectedTo.value) toAddr.value = selectedTo.value.address;
  if (selectedFrom.value) await suggestFor(selectedFrom.value.address as any, selectedFrom.value.decimals);
  // Initial auto-quote if amount prefilled
  if (amountInNumber.value > 0) await doQuote();
  // Observe theme changes
  try {
    themeObserver = new MutationObserver(() => syncTheme());
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  } catch (_) {}
});

watch(fromAddr, (addr) => {
  const t = tokens.value.find(t => t.address === addr);
  if (t) selectedFrom.value = t;
  // Zero out input when From token changes
  amountIn.value = '';
});
watch(toAddr, (addr) => {
  const t = tokens.value.find(t => t.address === addr);
  if (t) selectedTo.value = t;
});
watch(() => wallet.address, async () => {
  await load();
  await refreshBalances();
  if (selectedFrom.value) await suggestFor(selectedFrom.value.address as any, selectedFrom.value.decimals);
});
watch(() => chain.selectedId, async () => {
  await load();
  await refreshBalances();
  if (selectedFrom.value) fromAddr.value = selectedFrom.value.address;
  if (selectedTo.value) toAddr.value = selectedTo.value.address;
  if (selectedFrom.value) await suggestFor(selectedFrom.value.address as any, selectedFrom.value.decimals);
});
watch(selectedFrom, async (t) => {
  if (t) await suggestFor(t.address as any, t.decimals);
});
watch(toOptions, (opts) => {
  if (!opts || !opts.length) return;
  const exists = opts.some((t) => t.address === toAddr.value);
  if (!exists) toAddr.value = opts[0].address;
});

// Quick-select full balance as input
function useMax() {
  const bal = String(fromBalance.value || '').trim();
  if (!bal || bal === '0') return;
  amountIn.value = bal;
}

// Flip tokens (Uniswap-style)
function flipTokens() {
  const fa = fromAddr.value;
  fromAddr.value = toAddr.value;
  toAddr.value = fa;
}

// Auto-quote on changes with debounce
let quoteTimer: number | null = null;
watch([amountIn, fromAddr, toAddr], () => {
  if (quoteTimer) { clearTimeout(quoteTimer); quoteTimer = null; }
  if (!amountInNumber.value) return; // do not quote for empty/zero
  quoteTimer = window.setTimeout(() => { doQuote(); }, 350);
});

onBeforeUnmount(() => {
  if (themeObserver) try { themeObserver.disconnect(); } catch {}
});
</script>
